/**
 * FLOW[24341]: MS209 - Standardized React Query Defaults
 * Provides consistent error handling, retry logic, and error messages
 * across all API hooks in the application.
 *
 * FLOW[27264]: MS229 - Migrated to messageService for App context support
 *
 * @module hooks/api/useQueryDefaults
 */

import { messageService, notificationService } from '@/services/messageService';
import type { AxiosError } from 'axios';
import { isPublicApiPath, tokenStorage } from '@/api/client';
import { isAiShortCircuitError, resolveAiContourFamily } from '@/api/aiResilience';

// FLOW[27615]: Re-export messageService for direct use in hooks
export { messageService };

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * FLOW[24342]: Standard API error response structure
 */
export interface ApiErrorResponse {
  detail?: string | { msg: string; type: string }[];
  message?: string;
  error?: string | { code?: string; message?: string; detail?: string };
  errors?: Record<string, string[]>;
  data?: unknown;
  meta?: unknown;
}

/**
 * FLOW[24343]: Parsed error information for display
 */
export interface ParsedError {
  title: string;
  description: string;
  statusCode?: number;
  isAuthError: boolean;
  isValidationError: boolean;
  isNetworkError: boolean;
}

/**
 * FLOW[24396]: MS209 - Helper to extract string from detail field
 */
function extractDetailString(detail: string | { msg: string; type: string }[] | undefined): string | undefined {
  if (!detail) return undefined;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join('; ');
  return undefined;
}

function extractErrorString(
  errorValue:
    | string
    | { code?: string; message?: string; detail?: string }
    | undefined
): string | undefined {
  if (!errorValue) return undefined;
  if (typeof errorValue === 'string') return errorValue;
  if (typeof errorValue === 'object') {
    return (
      String(errorValue.message || '').trim() ||
      String(errorValue.detail || '').trim() ||
      String(errorValue.code || '').trim() ||
      undefined
    );
  }
  return undefined;
}

function isFreshIntegrationRequest(error: AxiosError<ApiErrorResponse>): boolean {
  const url = error.config?.url || '';
  return url.includes('/1c-fresh/');
}

// ============================================================================
// ERROR PARSING
// ============================================================================

/**
 * FLOW[24344]: Parse API error into user-friendly format
 */
export function parseApiError(error: AxiosError<ApiErrorResponse>): ParsedError {
  const statusCode = error.response?.status;
  const data = error.response?.data;
  const errorCode = String((error as { code?: string }).code || '').trim();
  const errorMessage = String(error.message || '').trim().toLowerCase();

  if (
    !error.response &&
    (errorCode === 'ECONNABORTED' ||
      errorCode === 'ETIMEDOUT' ||
      errorMessage.includes('timeout'))
  ) {
    return {
      title: 'Проверка ещё выполняется',
      description:
        'Сервис не успел ответить в ожидаемое время. Для больших TIFF и PDF это может занять дольше. Повторите запрос или откройте историю jobs через несколько секунд.',
      isAuthError: false,
      isValidationError: false,
      isNetworkError: true,
    };
  }

  // Network error (no response)
  if (!error.response) {
    return {
      title: 'Ошибка сети',
      description: 'Не удалось подключиться к серверу. Проверьте интернет-соединение.',
      isAuthError: false,
      isValidationError: false,
      isNetworkError: true,
    };
  }

  // Auth errors
  if (statusCode === 401) {
    if (isPublicApiPath(error.config?.url)) {
      return {
        title: 'Неверный API key',
        description:
          extractDetailString(data?.detail) ||
          data?.message ||
          'Проверьте tenant API key и попробуйте снова.',
        statusCode,
        isAuthError: false,
        isValidationError: false,
        isNetworkError: false,
      };
    }
    if (isFreshIntegrationRequest(error)) {
      return {
        title: 'Ошибка подключения 1C:Fresh',
        description:
          extractDetailString(data?.detail) ||
          data?.message ||
          'Проверьте логин, пароль и tenant_id для 1C:Fresh.',
        statusCode,
        isAuthError: false,
        isValidationError: false,
        isNetworkError: false,
      };
    }
    return {
      title: 'Требуется авторизация',
      description: 'Сессия истекла. Пожалуйста, войдите снова.',
      statusCode,
      isAuthError: true,
      isValidationError: false,
      isNetworkError: false,
    };
  }

  if (statusCode === 403) {
    return {
      title: 'Доступ запрещён',
      description: 'У вас нет прав для выполнения этого действия.',
      statusCode,
      isAuthError: true,
      isValidationError: false,
      isNetworkError: false,
    };
  }

  if (statusCode === 402) {
    return {
      title: 'Недостаточно кредитов',
      description:
        extractErrorString(data?.error) ||
        extractDetailString(data?.detail) ||
        data?.message ||
        'Пополните баланс credits и повторите запуск prepress check.',
      statusCode,
      isAuthError: false,
      isValidationError: false,
      isNetworkError: false,
    };
  }

  // Validation errors (422)
  if (statusCode === 422) {
    let description = 'Проверьте введённые данные.';

    if (data?.detail) {
      if (Array.isArray(data.detail)) {
        description = data.detail.map((d) => d.msg || d).join('; ');
      } else if (typeof data.detail === 'string') {
        description = data.detail;
      }
    } else if (data?.errors) {
      description = Object.entries(data.errors)
        .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
        .join('; ');
    }

    return {
      title: 'Ошибка валидации',
      description,
      statusCode,
      isAuthError: false,
      isValidationError: true,
      isNetworkError: false,
    };
  }

  // Not found (404)
  if (statusCode === 404) {
    return {
      title: 'Не найдено',
      description: extractDetailString(data?.detail) || data?.message || 'Запрашиваемый ресурс не найден.',
      statusCode,
      isAuthError: false,
      isValidationError: false,
      isNetworkError: false,
    };
  }

  // Conflict (409)
  if (statusCode === 409) {
    return {
      title: 'Конфликт данных',
      description: extractDetailString(data?.detail) || data?.message || 'Операция не может быть выполнена из-за конфликта.',
      statusCode,
      isAuthError: false,
      isValidationError: false,
      isNetworkError: false,
    };
  }

  // Server error (5xx)
  if (statusCode && statusCode >= 500) {
    return {
      title: 'Ошибка сервера',
      description: 'Произошла внутренняя ошибка. Попробуйте позже.',
      statusCode,
      isAuthError: false,
      isValidationError: false,
      isNetworkError: false,
    };
  }

  // Default error
  return {
    title: 'Ошибка',
    description:
      extractDetailString(data?.detail) ||
      data?.message ||
      extractErrorString(data?.error) ||
      'Произошла неизвестная ошибка.',
    statusCode,
    isAuthError: false,
    isValidationError: false,
    isNetworkError: false,
  };
}

// ============================================================================
// ERROR HANDLERS
// ============================================================================

/**
 * FLOW[24345]: Handle API error with appropriate UI feedback
 */
export function handleApiError(error: AxiosError<ApiErrorResponse>, options?: {
  showNotification?: boolean;
  showMessage?: boolean;
  redirectOnAuth?: boolean;
}): ParsedError {
  const { showNotification = false, showMessage = true, redirectOnAuth = true } = options || {};

  // FLOW[47473]: ЛЮБАЯ ошибка AI-контура (engagement-ai / ai-assistant) деградирует ТИХО —
  // никаких тостов из общего обработчика. UX-ом недоступности владеет circuit breaker
  // (максимум один предупреждающий тост на деградацию) + тихий inline-бейдж. Это гасит
  // «шторм окон с ошибками» как на короткозамкнутых, так и на первых реальных 404.
  if (
    isAiShortCircuitError(error) ||
    resolveAiContourFamily((error as AxiosError)?.config?.url)
  ) {
    return parseApiError(error);
  }

  const parsed = parseApiError(error);

  // Handle auth errors
  if (parsed.isAuthError && redirectOnAuth) {
    tokenStorage.clearTokens();
    // Don't redirect here - let the interceptor handle it
  }

  // Show UI feedback
  if (showNotification) {
    notificationService.error({
      title: parsed.title,
      description: parsed.description,
      duration: 5,
    });
  } else if (showMessage) {
    messageService.error(`${parsed.title}: ${parsed.description}`);
  }

  return parsed;
}

// ============================================================================
// QUERY DEFAULTS
// ============================================================================

/**
 * FLOW[24346]: Default options for useQuery hooks
 */
export const queryDefaults = {
  staleTime: 30 * 1000, // 30 seconds
  gcTime: 5 * 60 * 1000, // 5 minutes (was cacheTime)
  retry: (failureCount: number, error: unknown) => {
    // FLOW[47473]: короткозамкнутые ошибки AI-контура (circuit open) не ретраим —
    // сеть всё равно заблокирована интерсептором, повтор только жёг бы CPU и лог.
    if (isAiShortCircuitError(error)) {
      return false;
    }
    const status = (error as AxiosError | undefined)?.response?.status;
    // Don't retry on auth errors
    if (status === 401 || status === 403) {
      return false;
    }
    // Don't retry on validation errors
    if (status === 422) {
      return false;
    }
    // Don't retry on not found
    if (status === 404) {
      return false;
    }
    // Retry up to 2 times for other errors
    return failureCount < 2;
  },
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

/**
 * FLOW[24347]: Default options for useMutation hooks
 */
export const mutationDefaults = {
  retry: false, // Don't retry mutations by default
  onError: (error: Error) => {
    handleApiError(error as AxiosError<ApiErrorResponse>, {
      showMessage: true,
      redirectOnAuth: true,
    });
  },
};

/**
 * FLOW[47473]: Defaults для AUTO-mutation'ов AI-контуров (engagement-ai превью и т.п.).
 * Такие мутации срабатывают сами (открытие карточки/дашборда/ассистента), поэтому их ошибки
 * НЕ должны порождать тосты — недоступность видна через тихий inline-бейдж, а единственный
 * тост-предупреждение о деградации показывает circuit breaker (максимум один раз за сессию).
 */
export const aiSilentMutationDefaults = {
  retry: false,
  onError: () => {
    // Тихо: устойчивостью AI-контура управляет circuit breaker + inline-бейдж.
  },
};

// ============================================================================
// SUCCESS MESSAGES (Russian)
// ============================================================================

/**
 * FLOW[24348]: Standard success messages for CRUD operations
 */
export const successMessages = {
  create: (entityName: string) => `${entityName} успешно создан(а)`,
  update: (entityName: string) => `${entityName} успешно обновлён(а)`,
  delete: (entityName: string) => `${entityName} успешно удалён(а)`,
  save: (entityName: string) => `${entityName} успешно сохранён(а)`,
  approve: (entityName: string) => `${entityName} успешно одобрен(а)`,
  reject: (entityName: string) => `${entityName} успешно отклонён(а)`,
  cancel: (entityName: string) => `${entityName} успешно отменён(а)`,
  archive: (entityName: string) => `${entityName} успешно архивирован(а)`,
  restore: (entityName: string) => `${entityName} успешно восстановлен(а)`,
  // FLOW[25340]: MS217 - New operations for orders and SAGA management
  expedite: (entityName: string) => `${entityName} успешно ускорен(а)`,
  recover: (entityName: string) => `${entityName} успешно восстановлены`,
  cleanup: (entityName: string) => `${entityName} успешно очищены`,
};

/**
 * FLOW[24349]: Entity names in Russian for success messages
 */
export const entityNames = {
  order: 'Заказ',
  client: 'Клиент',
  product: 'Продукт',
  material: 'Материал',
  invoice: 'Счёт',
  payment: 'Платёж',
  production: 'Производство',
  lead: 'Лид',
  contact: 'Контакт',
  contract: 'Договор',
  template: 'Шаблон',
  category: 'Категория',
  user: 'Пользователь',
  // FLOW[24906]: MS214 - Added reservation entity name
  reservation: 'Резервирование',
  // FLOW[25341]: MS217 - New entities for orders and SAGA management
  priority: 'Приоритет',
  SAGAs: 'SAGA-процессы',
};

/**
 * FLOW[24350]: Show success message for mutation
 */
export function showSuccessMessage(operation: keyof typeof successMessages, entity: keyof typeof entityNames): void;
export function showSuccessMessage(operation: keyof typeof successMessages, entity: string): void;
/**
 * FLOW[28043]: MS234 - Show direct success message string
 */
export function showSuccessMessage(message: string): void;
export function showSuccessMessage(
  operationOrMessage: keyof typeof successMessages | string,
  entity?: keyof typeof entityNames | string
): void {
  if (entity !== undefined) {
    // Called with operation + entity
    const operation = operationOrMessage as keyof typeof successMessages;
    const entityName =
      entityNames[entity as keyof typeof entityNames] ?? entity;
    const text = successMessages[operation](entityName);
    messageService.success(text);
  } else {
    // Called with direct message string
    messageService.success(operationOrMessage);
  }
}
